import type { ScheduleItem } from '../stores/scheduleStore';

export interface AssigneeEntry {
  id: number;
  kind: 'person' | 'team';
}

/**
 * Build the full `personsAndTeams` array for a monday.com people column update,
 * preserving all existing assignees of the same monday item and swapping only
 * the dragged/changed one.
 *
 * @param allItems      All items currently in the schedule store
 * @param mondayItemId  The monday.com item ID being reassigned
 * @param oldAssigneeId The previous assigneeId (person) being replaced (0 = was unassigned/team)
 * @param oldTeamId     The previous assignedTeamId being replaced (undefined = not a team row)
 * @param newAssigneeId The new assigneeId to assign (0 = dropping on team header)
 * @param newTeamId     The new team ID if dropping onto a team row (undefined = person row)
 * @param kind          Whether the new assignment is 'person' or 'team'
 * @param addOnly       When true, keep the old assignee and just add the new one (Alt+drag split)
 */
export function buildReassignedEntries(
  allItems: ScheduleItem[],
  mondayItemId: number,
  oldAssigneeId: number,
  oldTeamId: number | undefined,
  newAssigneeId: number,
  newTeamId: number | undefined,
  kind: 'person' | 'team',
  addOnly = false,
): AssigneeEntry[] {
  // Collect all sibling schedule items that share the same monday.com item
  const siblings = allItems.filter((i) => i.mondayItemId === mondayItemId);

  // Build a set of all existing assignee entries from siblings.
  // Only treat assignedTeamId as a real team assignment when the item is
  // team-only (assigneeId === 0). In teamWithUsers mode, person-assigned items
  // carry assignedTeamId as a UI-only field (to track which team row they
  // belong to) — it should NOT be sent as a team assignment to monday.com.
  const entries = new Map<string, AssigneeEntry>();
  for (const sib of siblings) {
    if (sib.assigneeId > 0) {
      entries.set(`person-${sib.assigneeId}`, { id: sib.assigneeId, kind: 'person' });
    } else if (sib.assignedTeamId && sib.assignedTeamId > 0) {
      entries.set(`team-${sib.assignedTeamId}`, { id: sib.assignedTeamId, kind: 'team' });
    }
  }

  // Remove the old assignee being swapped out (skip in addOnly / split mode)
  if (!addOnly) {
    if (oldAssigneeId > 0) {
      entries.delete(`person-${oldAssigneeId}`);
    }
    if (oldTeamId && oldTeamId > 0) {
      entries.delete(`team-${oldTeamId}`);
    }
  }

  // Add the new assignee
  if (kind === 'person' && newAssigneeId > 0) {
    entries.set(`person-${newAssigneeId}`, { id: newAssigneeId, kind: 'person' });
  } else if (kind === 'team' && newTeamId && newTeamId > 0) {
    entries.set(`team-${newTeamId}`, { id: newTeamId, kind: 'team' });
  }

  return Array.from(entries.values());
}

/**
 * Redistribute hours across all sibling ScheduleItems that share the
 * same `mondayItemId`. Call this after adding/removing an assignee so event
 * cards and workload indicators reflect the correct hours.
 *
 * When `perPersonDistribution` is true, divides the raw total evenly.
 * When false, every assignee gets the full raw hours.
 *
 * Uses the raw column values (`hoursPerDay` / `hoursTotal`) which represent
 * the un-split total for the monday.com item.
 *
 * @param updateItem  Store's updateItem function
 * @param allItems    All items currently in the schedule store
 * @param mondayItemId  The monday.com item to redistribute hours for
 * @param perPersonDistribution  Whether to divide hours among assignees
 */
export function redistributeHours(
  updateItem: (id: string, updates: Partial<ScheduleItem>) => void,
  allItems: ScheduleItem[],
  mondayItemId: number,
  perPersonDistribution = true,
): void {
  const siblings = allItems.filter(
    (i) => i.mondayItemId === mondayItemId && i.assigneeId > 0,
  );
  if (siblings.length === 0) return;

  // Recover the raw total from the first sibling (all siblings share the same raw column values)
  const ref = siblings[0];
  const rawHours = ref.workHourFormat === 'per_day' ? ref.hoursPerDay : ref.hoursTotal;
  const targetHours = (perPersonDistribution && siblings.length > 1)
    ? Math.round((rawHours / siblings.length) * 100) / 100
    : rawHours;

  for (const sib of siblings) {
    const updates: Partial<ScheduleItem> = {};
    if (sib.hours !== targetHours) updates.hours = targetHours;
    if (sib.assigneeCount !== siblings.length) updates.assigneeCount = siblings.length;
    if (sib.originalTotalHours !== rawHours) updates.originalTotalHours = rawHours;
    if (Object.keys(updates).length > 0) {
      updateItem(sib.id, updates);
    }
  }
}

/**
 * Sync timeline (startDate / endDate) across all sibling ScheduleItems that
 * share the same `mondayItemId`. Because monday.com stores a single timeline
 * column per item, all per-assignee clones must stay in sync.
 *
 * Call this after any drag/resize so the other assignees' events move too.
 *
 * @param updateItem   Store's updateItem function
 * @param allItems     All items currently in the schedule store
 * @param mondayItemId The monday.com item whose siblings should sync
 * @param startDate    New start date (ISO string)
 * @param endDate      New end date (ISO string)
 * @param excludeId    The item ID that was already updated (skip to avoid no-op)
 */
export function syncSiblingTimelines(
  updateItem: (id: string, updates: Partial<ScheduleItem>) => void,
  allItems: ScheduleItem[],
  mondayItemId: number,
  startDate: string,
  endDate: string,
  excludeId: string,
): void {
  for (const sib of allItems) {
    if (
      sib.mondayItemId === mondayItemId &&
      sib.id !== excludeId &&
      (sib.startDate !== startDate || sib.endDate !== endDate)
    ) {
      updateItem(sib.id, { startDate, endDate });
    }
  }
}
